import React, { useState } from 'react';
import { SplitLayout } from '../components/SplitLayout';
import { SectionsOverview } from '../components/SectionsOverview';
import { PersonalInfoJourney } from '../components/journeys/PersonalInfoJourney';
import { BusinessInfoJourney } from '../components/journeys/BusinessInfoJourney';
import { DocumentsJourney } from '../components/journeys/DocumentsJourney';
import { UpdateDocumentJourney } from '../components/journeys/UpdateDocumentJourney';
import { BankAccountJourney } from '../components/journeys/BankAccountJourney';
import { ReviewSubmitJourney } from '../components/journeys/ReviewSubmitJourney';
import { AccountSecurityJourney } from '../components/journeys/AccountSecurityJourney';
import { MerchantSignup } from '../components/MerchantSignup';
import { OTPVerification } from '../components/OTPVerification';
import { EmailVerification } from '../components/EmailVerification';
import { Button } from '../components/ui/Button';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { SAMPLE_BUSINESS_INFO, SAMPLE_PERSONAL_INFO, buildSampleBankAccount, SAMPLE_ACCOUNT_SECURITY, buildSampleDocuments } from '../constants/sampleData';
// Review in progress component
const ReviewInProgressSection = ({
  onGoToDashboard,
  onReturnApplication,
  hasResubmitted = false
}: {
  onGoToDashboard?: () => void;
  onReturnApplication?: () => void;
  hasResubmitted?: boolean;
}) => {
  // Fixed demo timestamps so the timeline tells a consistent story across
  // first-submission and resubmission states during a live walkthrough.
  const originalSubmitDate = '06 May 2026, 10:30 am';
  const actionRequiredDate = '06 May 2026, 10:40 am';
  const formattedSubmitted = hasResubmitted ? '06 May 2026, 10:45 am' : originalSubmitDate;
  return <div className="flex flex-col h-full py-8 max-w-lg mx-auto animate-in fade-in duration-500" data-id="element-1210">
      {/* Maybank yellow header card */}
      <div className="rounded-2xl bg-[#FFCC00] p-6 mb-6 text-center" data-id="element-1211">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black/10 mb-4" data-id="element-1212">
          <CheckCircle2 className="w-9 h-9 text-black" strokeWidth={2.25} aria-hidden="true" data-id="element-1213" />
        </div>
        <h2 className="text-2xl font-bold text-black mb-1" data-id="element-1214">
          Review in Progress
        </h2>
        <p className="text-sm text-black/80 leading-relaxed" data-id="element-1215">
          Thank you for completing your application! Our team will get back to
          you within <span className="font-semibold" data-id="element-1216">2–3 business days</span>.
        </p>
      </div>

      {/* Status badge row */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-id="element-1217">
        <div className="flex items-center justify-between" data-id="element-1218">
          <span className="text-sm text-gray-600" data-id="element-1219">Application Status</span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-id="element-1220">
            Under Review
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-8" data-id="element-1221">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-900 mb-4" data-id="element-1222">
          Application Timeline
        </h3>
        <ol className="relative space-y-5" data-id="element-1223">
          {/* Connector line */}
          <span aria-hidden="true" className="absolute left-3.5 top-2 bottom-2 w-px bg-gray-200" data-id="element-1224" />

          {hasResubmitted ? <>
              {/* Step 1 — Original Submission */}
              <li className="relative flex items-start gap-3" data-id="element-1225">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-green-100 border-2 border-green-600 flex-shrink-0" data-id="element-1226">
                  <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" data-id="element-1227" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1228">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1229">
                    Application Submitted
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1230">
                    {originalSubmitDate}
                  </p>
                </div>
              </li>

              {/* Step 2 — Action Required */}
              <li className="relative flex items-start gap-3" data-id="element-1231">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-red-100 border-2 border-red-600 flex-shrink-0" data-id="element-1232">
                  <CheckCircle2 className="w-4 h-4 text-red-700" aria-hidden="true" data-id="element-1233" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1234">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1235">
                    Action Required
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1236">
                    {actionRequiredDate}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-1237">
                    Bank requested updates to Supporting Documents.
                  </p>
                </div>
              </li>

              {/* Step 3 — Resubmitted */}
              <li className="relative flex items-start gap-3" data-id="element-1238">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-green-100 border-2 border-green-600 flex-shrink-0" data-id="element-1239">
                  <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" data-id="element-1240" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1241">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1242">
                    Application Resubmitted
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1243">
                    {formattedSubmitted}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-1244">
                    Updates provided.
                  </p>
                </div>
              </li>

              {/* Step 4 — In progress */}
              <li className="relative flex items-start gap-3" data-id="element-1245">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-[#FFCC00]/20 border-2 border-[#FFCC00] flex-shrink-0" data-id="element-1246">
                  <Loader2 className="w-4 h-4 text-gray-900 animate-spin" aria-hidden="true" data-id="element-1247" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1248">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1249">
                    Bank Admin Review
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1250">
                    In progress. Reviewing updated documents...
                  </p>
                </div>
              </li>
            </> : <>
              {/* Step 1 — completed */}
              <li className="relative flex items-start gap-3" data-id="element-1251">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-green-100 border-2 border-green-600 flex-shrink-0" data-id="element-1252">
                  <CheckCircle2 className="w-4 h-4 text-green-700" aria-hidden="true" data-id="element-1253" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1254">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1255">
                    Application Submitted
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1256">
                    {formattedSubmitted}
                  </p>
                </div>
              </li>
              {/* Step 2 — in progress */}
              <li className="relative flex items-start gap-3" data-id="element-1257">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-[#FFCC00]/20 border-2 border-[#FFCC00] flex-shrink-0" data-id="element-1258">
                  <Loader2 className="w-4 h-4 text-gray-900 animate-spin" aria-hidden="true" data-id="element-1259" />
                </span>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-1260">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1261">
                    Bank Admin Review
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-1262">In progress…</p>
                </div>
              </li>
            </>}
        </ol>
      </div>

      {/* Primary CTA */}
      <Button fullWidth onClick={onGoToDashboard} data-id="element-1263">
        Go to Merchant Dashboard
        <ArrowRight className="w-4 h-4 ml-1.5" aria-hidden="true" data-id="element-1264" />
      </Button>

      {onReturnApplication && !hasResubmitted && <Button variant="outline" fullWidth className="mt-3" onClick={onReturnApplication} data-id="element-1265">
          Demo: Return Application (Partial Rejection)
        </Button>}

      <p className="text-xs text-gray-600 text-center mt-3 leading-relaxed" data-id="element-1266">
        You can explore the portal, but payment processing is disabled until
        approval.
      </p>
    </div>;
};
type ViewState = 'phone-entry' | 'otp-verification' | 'email-verification' | 'overview' | 'personal-info' | 'business-info' | 'documents' | 'bank-account' | 'account-security' | 'review' | 'submitted';
interface OnboardingFlowProps {
  onGoToDashboard?: () => void;
}
export function OnboardingFlow({
  onGoToDashboard
}: OnboardingFlowProps) {
  const [currentView, setCurrentView] = useState<ViewState>('phone-entry');
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [personalInfoData, setPersonalInfoData] = useState<any>(null);
  const [businessInfoData, setBusinessInfoData] = useState<any>(null);
  const [documentsData, setDocumentsData] = useState<any>(null);
  const [bankAccountData, setBankAccountData] = useState<any>(null);
  const [accountSecurityData, setAccountSecurityData] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [signupEmail, setSignupEmail] = useState<string>('');
  const [signupName, setSignupName] = useState<string>('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isApplicationReturned, setIsApplicationReturned] = useState(false);
  const [hasResubmitted, setHasResubmitted] = useState(false);
  const [flaggedSections, setFlaggedSections] = useState<string[]>([]);
  // When the user clicks "Edit" from Review, we remember to send them back to
  // Review on save instead of bouncing through the dashboard hub.
  const [returnToView, setReturnToView] = useState<ViewState>('overview');
  const handleStartSection = (sectionId: string) => {
    // Direct navigation from the dashboard always returns to the dashboard.
    setReturnToView('overview');
    setCurrentView(sectionId as ViewState);
  };
  const markCompleteAndReturn = (sectionId: string) => {
    setCompletedSections(prev => prev.includes(sectionId) ? prev : [...prev, sectionId]);
    setCurrentView(returnToView);
    setReturnToView('overview');
  };
  const handlePersonalInfoComplete = (data: any) => {
    setPersonalInfoData(data);
    markCompleteAndReturn('personal-info');
  };
  const handleBusinessInfoComplete = (data: any) => {
    setBusinessInfoData(data);
    markCompleteAndReturn('business-info');
  };
  const handleDocumentsComplete = (data: any) => {
    setDocumentsData(data);
    markCompleteAndReturn('documents');
  };
  const handleBankAccountComplete = (data: any) => {
    setBankAccountData(data);
    markCompleteAndReturn('bank-account');
  };
  const handleBackToOverview = () => {
    // Cancel from a section returns to wherever the user came from.
    setCurrentView(returnToView);
    setReturnToView('overview');
  };
  // Demo helper: pre-populates every section with realistic Malaysian data
  // so stakeholders can jump straight to Review & Submit.
  const handleFillSampleData = () => {
    setBusinessInfoData(SAMPLE_BUSINESS_INFO);
    setPersonalInfoData(SAMPLE_PERSONAL_INFO);
    setDocumentsData(buildSampleDocuments());
    setBankAccountData(buildSampleBankAccount());
    setAccountSecurityData(SAMPLE_ACCOUNT_SECURITY);
    setCompletedSections(['business-info', 'personal-info', 'documents', 'bank-account', 'account-security']);
  };
  const handleResetData = () => {
    setBusinessInfoData(null);
    setPersonalInfoData(null);
    setDocumentsData(null);
    setBankAccountData(null);
    setAccountSecurityData(null);
    setCompletedSections([]);
  };
  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setCurrentView('otp-verification');
  };
  const handleSignupSubmit = (data: {
    method: 'email' | 'phone';
    contact: string;
    fullName: string;
    country: string;
  }) => {
    setSignupName(data.fullName);
    if (data.method === 'phone') {
      setPhoneNumber(data.contact);
      setCurrentView('otp-verification');
    } else {
      setSignupEmail(data.contact);
      setCurrentView('email-verification');
    }
  };
  const handleOTPVerify = () => {
    setIsPhoneVerified(true);
    setCurrentView('overview');
  };
  const handleEmailVerify = () => {
    setIsPhoneVerified(true);
    setCurrentView('overview');
  };
  const handleBackToPhoneEntry = () => {
    setCurrentView('phone-entry');
  };
  const handleReturnApplication = () => {
    setIsApplicationReturned(true);
    setFlaggedSections(['documents']);
    setCurrentView('overview');
  };
  // Illustration content changes based on view
  const getIllustrationContent = () => {
    switch (currentView) {
      case 'phone-entry':
        return {
          title: 'Create Your Merchant Account',
          description: 'Join thousands of Malaysian businesses accepting payments with Maybank.'
        };
      case 'otp-verification':
        return {
          title: 'Verify Your Identity',
          description: 'Enter the code we sent to your phone to confirm your identity and proceed.'
        };
      case 'email-verification':
        return {
          title: 'Verify Your Email',
          description: 'Confirm your email so we can keep your Maybank Merchant account secure.'
        };
      case 'personal-info':
        return {
          title: "Let's get to know you",
          description: 'We need your personal details to verify your identity and keep your account secure.'
        };
      case 'business-info':
        return {
          title: 'Tell us about your business',
          description: 'We need to understand your business structure and activity to set up your account correctly.'
        };
      case 'documents':
        return {
          title: 'Upload your documents',
          description: 'We need to verify your identity and business with supporting documents to complete your account setup.'
        };
      case 'bank-account':
        return {
          title: 'Link your bank account',
          description: 'Connect your business bank account to receive payments and manage your finances.'
        };
      case 'account-security':
        return {
          title: 'Secure your account',
          description: 'Choose an authentication method to protect your account in line with Bank Negara Malaysia regulations.'
        };
      case 'review':
        return {
          title: 'One last look',
          description: 'Take a moment to confirm everything is correct before we send your application to Maybank for verification.'
        };
      case 'submitted':
        return {
          title: 'Application Under Review',
          description: 'Your application has been submitted successfully. Our team is carefully reviewing your information to ensure everything is in order.'
        };
      case 'overview':
      default:
        if (isApplicationReturned) {
          return {
            title: 'Action Required',
            description: 'Please review the notes from our team and update the flagged sections to continue.'
          };
        }
        return {
          title: 'Welcome to Onboarding',
          description: 'Complete these steps to get your account fully set up and ready to go.'
        };
    }
  };
  const illustration = getIllustrationContent();
  return <SplitLayout title={illustration.title} description={illustration.description} data-id="element-1267">
      <div className="h-full" data-id="element-1268">
        {currentView === 'phone-entry' && <MerchantSignup onContinue={handleSignupSubmit} data-id="element-1269" />}

        {currentView === 'otp-verification' && <OTPVerification phone={phoneNumber} onVerify={handleOTPVerify} onBack={handleBackToPhoneEntry} data-id="element-1270" />}

        {currentView === 'email-verification' && <EmailVerification email={signupEmail} onVerify={handleEmailVerify} onBack={handleBackToPhoneEntry} data-id="element-1271" />}

        {currentView === 'overview' && <SectionsOverview completedSections={completedSections} onStartSection={handleStartSection} onFillSampleData={handleFillSampleData} onResetData={handleResetData} rejectionState={isApplicationReturned ? {
        isRejected: true,
        flaggedSections: flaggedSections,
        adminNotes: {
          documents: 'The SSM Section 17 document provided is blurry and unreadable. Please upload a clearer scanned copy.'
        }
      } : undefined} data-id="element-1272" />}

        {currentView === 'personal-info' && <PersonalInfoJourney onComplete={handlePersonalInfoComplete} onExit={handleBackToOverview} prefillEmail={signupEmail} prefillName={signupName} prefillPhone={phoneNumber} businessAddress={businessInfoData?.address} initialData={personalInfoData} data-id="element-1273" />}

        {currentView === 'business-info' && <BusinessInfoJourney onComplete={handleBusinessInfoComplete} onExit={handleBackToOverview} prefillSupportPhone={phoneNumber} initialData={businessInfoData} data-id="element-1274" />}

        {currentView === 'documents' && (isApplicationReturned && flaggedSections.includes('documents') ? <UpdateDocumentJourney onComplete={() => {
        // Clear the flag to unlock the Review & Submit section
        setFlaggedSections(prev => prev.filter(id => id !== 'documents'));
        handleDocumentsComplete(documentsData);
      }} onCancel={handleBackToOverview} adminNote="The SSM Section 17 document provided is blurry and unreadable. Please upload a clearer scanned copy." data-id="element-1275" /> : <DocumentsJourney businessType={businessInfoData?.businessType} idMethod={personalInfoData?.idMethod} onComplete={handleDocumentsComplete} onExit={handleBackToOverview} initialData={documentsData} data-id="element-1276" />)}

        {currentView === 'bank-account' && <BankAccountJourney onComplete={handleBankAccountComplete} onExit={handleBackToOverview} initialData={bankAccountData} data-id="element-1277" />}

        {currentView === 'account-security' && <AccountSecurityJourney onComplete={data => {
        setAccountSecurityData(data);
        markCompleteAndReturn('account-security');
      }} onExit={handleBackToOverview} initialData={accountSecurityData} data-id="element-1278" />}

        {currentView === 'review' && <ReviewSubmitJourney businessInfo={businessInfoData} personalInfo={personalInfoData} documents={documentsData} bankAccount={bankAccountData} accountSecurity={accountSecurityData} onEditSection={id => {
        // Edit from Review should bounce the user back to Review on save,
        // not all the way out to the dashboard.
        setReturnToView('review');
        setCurrentView(id as ViewState);
      }} onExit={handleBackToOverview} onSubmit={() => {
        setCompletedSections(prev => prev.includes('review') ? prev : [...prev, 'review']);
        if (isApplicationReturned) {
          setHasResubmitted(true);
        }
        setIsApplicationReturned(false);
        setFlaggedSections([]);
        setCurrentView('submitted');
      }} data-id="element-1279" />}

        {currentView === 'submitted' && <ReviewInProgressSection onGoToDashboard={onGoToDashboard} onReturnApplication={handleReturnApplication} hasResubmitted={hasResubmitted} data-id="element-1280" />}
      </div>
    </SplitLayout>;
}